import {
  Item,
  ItemGroup,
  SelectorProps,
  useSelector,
} from "@/app/providers/selector_provider"
import { RawHTML } from "@/app/raw_html"
import { EntityValues, Formatter, Metric, Term } from "@/app/types"
import { Button } from "@/components/buttons/button"
import { availableMetricsArrayFilter } from "@/components/comparison_group_list/comparison_group_list.functions"
import { Dispatch, SetStateAction, useMemo } from "react"

interface FilteredMetricsButton {
  isFund: boolean
  metric: Metric
  setMetric: Dispatch<SetStateAction<Metric>>
  entityValues: EntityValues
  term: Term
  isOverviewPage?: boolean
  className?: string
}

export function FilteredMetricsButton({
  isFund,
  metric,
  setMetric,
  entityValues,
  term,
  isOverviewPage,
  className,
}: FilteredMetricsButton) {
  const { showSelector, isSelectorShown } = useSelector()

  const formatters = Formatter.getFormatter(metric.name)

  const metricList = useMemo(() => {
    return isOverviewPage ?
        Metric.ALL_METRICS.filter((m) => !m.firmOnly).sort((a, b) =>
          a.name.localeCompare(b.name)
        )
      : Metric.ALL_METRICS.filter(
          (m) => m.fundOnly === isFund || m.firmOnly === !isFund
        ).sort((a, b) => a.name.localeCompare(b.name))
  }, [isFund])

  const filteredMetricList = useMemo(() => {
    return metricList.filter((metric) => {
      const availableMetrics = availableMetricsArrayFilter(
        isFund,
        entityValues,
        metricList,
        term
      )

      if (availableMetrics) return availableMetrics.includes(metric.name)
    })
  }, [isFund, entityValues, metricList, term])

  const metricOptions: ItemGroup = useMemo(
    () =>
      ({
        key: "metric_options",
        text: "Metric Options",
        disabled: false,
        items: filteredMetricList.map(
          (m) =>
            ({
              key: m.name,
              text: m.text,
              disabled: false,
              infoText: m.text,
            }) as Item
        ),
      }) as ItemGroup,
    [filteredMetricList]
  )

  const metricSelector: SelectorProps = {
    options: [metricOptions],
    selectedKey: metric.name,
    onSelect: (metricName) =>
      setMetric((prevState) => {
        const metricFound = filteredMetricList.find(
          (m) => m.name === metricName
        )
        if (!metricFound) return prevState
        else return metricFound
      }),
  }

  return (
    <Button
      className={`min-w-32 ${className}`}
      color="secondary"
      onClick={() => !isSelectorShown && showSelector(metricSelector)}>
      <RawHTML>{formatters(metric.text, 0, true)}</RawHTML>
    </Button>
  )
}
